<script setup lang="ts">
import { LoginView } from "@/authentication/types/LoginView";
import AuthCardShell from '@/application/components/AuthCardShell.vue';
import { ref, watch } from 'vue';
import TheEmailLoginForm from "@/authentication/components/TheEmailLoginForm.vue";
import ThePasswortLoginForm from "@/authentication/components/ThePasswortLoginForm.vue";
import TwoFactorChallengeForm from "@/authentication/components/TwoFactorChallengeForm.vue";
import { useRoute, useRouter } from "vue-router";
import { Finanzmanager } from "@/application/router/types/types";
import { useUserStore } from "@/user/store/userStore";
import { SecretNames, setSecret } from "@/application/plugins/secretStore";
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { captureException } from "@sentry/vue";

const router = useRouter();
const route = useRoute();

const view = ref<LoginView>(LoginView.Password);
const requestAccessToken = ref('');
const loading = ref(false);

function continueWithToken(token: string) {
  requestAccessToken.value = token;
  view.value = LoginView.TwoFactor;
}

// Invoked via the `logging-in` event emitted by TwoFactorChallengeForm. Vue does not await a
// component event listener's returned promise, so any rejection here would otherwise surface as an
// unhandled promise rejection attributed to `/login`. Keep the whole flow inside try/catch.
async function loggingIn(accessToken?: string, clientSecret?: string) {
  loading.value = true;
  try {
    if (accessToken && clientSecret) {
      await Promise.all([
        setSecret(SecretNames.AccessToken, accessToken),
        setSecret(SecretNames.ClientSecret, clientSecret),
      ]);
    }

    await useUserStore().onLoggedIn();
    const redirect = route.query?.redirect ?? null;

    if (typeof redirect === 'string') {
      await router.push(decodeURIComponent(redirect));
    } else {
      await router.push({ name: Finanzmanager.VERTRAEGE });
    }
  } catch (error) {
    captureException(error);
    view.value = LoginView.Password;
  } finally {
    loading.value = false;
  }
}

watch(() => route.query.requestAccessToken,
  (token: unknown) => {
    if (token) {
      continueWithToken(token as string)
    }
  },
  { immediate: true },
);
</script>

<template>
  <AuthCardShell>
    <div v-if="loading" class="relative text-center">
      <FaIcon class="mr-2 text-blue-700" name="spinner-third" variant="duotone" spin size="2x" />
    </div>

    <TheEmailLoginForm
      v-else-if="view === LoginView.Email"
      @switch-to-password="view = LoginView.Password"
    />

    <ThePasswortLoginForm
      v-else-if="view === LoginView.Password"
      @switch-to-mail="view = LoginView.Email"
      @two-factor-challenge="continueWithToken"
    />

    <TwoFactorChallengeForm
      v-else-if="view === LoginView.TwoFactor"
      :request-access-token="requestAccessToken"
      @switch-to-password="view = LoginView.Password"
      @logging-in="loggingIn"
    />
  </AuthCardShell>
</template>
